export const getBrowserStorage = (area = 'local') => {
  if (typeof window === 'undefined') return null;
  return area === 'session' ? window.sessionStorage : window.localStorage;
};

export const safeParseJSON = (rawValue, fallback = null) => {
  if (rawValue == null) return fallback;
  if (typeof rawValue !== 'string') return rawValue;
  try {
    return JSON.parse(rawValue);
  } catch {
    return fallback;
  }
};

export const readStorageItem = (key, { area = 'local', fallback = null } = {}) => {
  if (!key) return fallback;
  try {
    const storage = getBrowserStorage(area);
    if (!storage) return fallback;
    const value = storage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
};

export const writeStorageItem = (key, value, { area = 'local' } = {}) => {
  if (!key) return false;
  try {
    const storage = getBrowserStorage(area);
    if (!storage) return false;
    if (value === undefined || value === null) {
      storage.removeItem(key);
      return true;
    }
    storage.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
};

export const removeStorageItem = (key, { area = 'local' } = {}) => {
  if (!key) return false;
  try {
    const storage = getBrowserStorage(area);
    if (!storage) return false;
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export const readStorageJSON = (key, { area = 'local', fallback = null } = {}) => {
  const raw = readStorageItem(key, { area, fallback: null });
  if (raw === null) return fallback;
  return safeParseJSON(raw, fallback);
};

export const writeStorageJSON = (key, value, { area = 'local' } = {}) => {
  if (value === undefined) {
    return removeStorageItem(key, { area });
  }
  try {
    const storage = getBrowserStorage(area);
    if (!storage) return false;
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

export const resolveStoredAuthToken = ({
  explicitToken,
  authTokenKey = 'authToken',
  legacyTokenKey = null,
  allowAccessToken = false,
} = {}) => {
  if (typeof explicitToken === 'string' && explicitToken.trim()) return explicitToken;
  if (explicitToken && typeof explicitToken === 'object') {
    if (typeof explicitToken.token === 'string' && explicitToken.token.trim()) {
      return explicitToken.token;
    }
    if (
      allowAccessToken &&
      typeof explicitToken.accessToken === 'string' &&
      explicitToken.accessToken.trim()
    ) {
      return explicitToken.accessToken;
    }
  }
  if (explicitToken === null) return null;

  const sessionToken = readStorageItem(authTokenKey, { area: 'session', fallback: null });
  if (sessionToken) {
    const parsed = safeParseJSON(sessionToken, sessionToken);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object' && typeof parsed.token === 'string') {
      return parsed.token;
    }
    if (
      allowAccessToken &&
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.accessToken === 'string'
    ) {
      return parsed.accessToken;
    }
  }

  const persisted = readStorageItem(authTokenKey, { fallback: null });
  if (persisted) {
    const parsed = safeParseJSON(persisted, persisted);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object' && typeof parsed.token === 'string') {
      return parsed.token;
    }
    if (
      allowAccessToken &&
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.accessToken === 'string'
    ) {
      return parsed.accessToken;
    }
  }

  return legacyTokenKey ? readStorageItem(legacyTokenKey, { fallback: null }) : null;
};
